import { type Component, createSignal, createResource, createMemo, For, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { A, useNavigate } from '@solidjs/router'
import { usePolls } from '../../../shared/providers/coris/coris-polls.provider'
import { useUser } from '../../../shared/providers/user-provider'
import { AppRoutes } from '../../../shared/app-routes'
import { Icon } from '@iconify-icon/solid'
import type { PollSummary, PollState } from '../domain/models/poll.model'
import Dialog from '../../../components/shared/components/Dialog/Dialog'
import { sticky } from '../../../shared/directives/sticky'
import { SEGMENT_BG_CLASSES } from './segment-colors'

sticky

const STATE_LABEL: Record<PollState, string> = {
  open: 'Open',
  closed: 'Closed'
}

const STATE_CLASSES: Record<PollState, string> = {
  open: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-700'
}

interface HoverState {
  poll: PollSummary
  x: number
  y: number
}

const PollsPage: Component = () => {
  const navigate = useNavigate()
  const { getPolls, deletePoll } = usePolls()
  const { organizationSlug } = useUser()
  const [search, setSearch] = createSignal('')
  const [deleteConfirmPostId, setDeleteConfirmPostId] = createSignal<number | null>(null)
  const [refreshKey, setRefreshKey] = createSignal(0)
  const [isStuck, setIsStuck] = createSignal(false)
  const [hovered, setHovered] = createSignal<HoverState | null>(null)

  const showPopover = (poll: PollSummary, target: HTMLElement): void => {
    const rect = target.getBoundingClientRect()
    setHovered({ poll, x: rect.left + rect.width / 2, y: rect.top })
  }
  const hidePopover = (): void => {
    setHovered(null)
  }

  const [allPolls] = createResource(
    () => {
      const slug = organizationSlug()
      if (!slug) return null
      return { refresh: refreshKey(), slug }
    },
    async () => await getPolls.execute()
  )

  const polls = createMemo(() => {
    const list = allPolls()
    if (!list) return undefined
    const term = search().trim().toLowerCase()
    if (!term) return list
    return list.filter((p) => p.question.toLowerCase().includes(term))
  })

  const handleDelete = async (postId: number): Promise<void> => {
    await deletePoll.execute(postId)
    setDeleteConfirmPostId(null)
    setRefreshKey((k) => k + 1)
  }

  const formatDate = (d: Date): string =>
    `${d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString(
      'en-US',
      { hour: '2-digit', minute: '2-digit', hour12: true }
    )}`

  return (
    <div class="flex w-full flex-col p-6">
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 -mx-6 px-6 py-6 transition-colors duration-200"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent': !isStuck()
        }}
      >
        <div class="mx-auto w-full max-w-[1280px] flex items-start gap-8">
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2.5 rounded-md border border-gray-200 bg-white px-3 py-2">
              <div class="text-gray-400">
                <Icon icon="heroicons:magnifying-glass" class="text-[1rem]" />
              </div>
              <input
                type="text"
                placeholder="Search by poll question"
                value={search()}
                onInput={(e) => setSearch(e.currentTarget.value)}
                class="flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
              />
            </div>
          </div>
          <A
            href={AppRoutes.PollsNew()}
            class="flex-shrink-0 px-6 py-2 bg-white rounded-full shadow-sm border border-gray-200 flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            <Icon icon="heroicons:plus" class="text-[1rem]" />
            New poll
          </A>
        </div>
      </div>

      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-8 pt-6">
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table class="min-w-full divide-y divide-gray-200">
            <thead>
              <tr class="bg-gray-50">
                <th class="px-4 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-b border-gray-200">
                  Question
                </th>
                <th class="px-4 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-b border-gray-200 w-32">
                  State
                </th>
                <th class="px-4 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-b border-gray-200 w-28">
                  Votes
                </th>
                <th class="px-4 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-b border-gray-200 w-24">
                  Post
                </th>
                <th class="px-4 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-b border-gray-200 w-64">
                  Closes at
                </th>
                <th class="px-4 py-3.5 text-center text-sm font-medium text-gray-900 border-r border-b border-gray-200 w-28">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <Show
                when={!allPolls.loading}
                fallback={
                  <For each={[1, 2, 3]}>
                    {() => (
                      <tr class="animate-pulse">
                        <td class="px-4 py-3 border-r border-gray-200">
                          <div class="h-5 bg-gray-200 rounded w-64" />
                        </td>
                        <td class="px-4 py-3 border-r border-gray-200 w-32">
                          <div class="h-5 bg-gray-200 rounded w-16" />
                        </td>
                        <td class="px-4 py-3 border-r border-gray-200 w-28">
                          <div class="h-5 bg-gray-200 rounded w-8" />
                        </td>
                        <td class="px-4 py-3 border-r border-gray-200 w-24">
                          <div class="h-5 bg-gray-200 rounded w-12" />
                        </td>
                        <td class="px-4 py-3 border-r border-gray-200 w-64">
                          <div class="h-5 bg-gray-200 rounded w-40" />
                        </td>
                        <td class="px-4 py-3 border-r border-gray-200 w-28">
                          <div class="h-5 bg-gray-200 rounded w-8 mx-auto" />
                        </td>
                      </tr>
                    )}
                  </For>
                }
              >
                <For
                  each={polls()}
                  fallback={
                    <tr>
                      <td colspan="6" class="px-4 py-12 text-center text-sm text-gray-500">
                        No polls yet. Click "New poll" to create one.
                      </td>
                    </tr>
                  }
                >
                  {(item: PollSummary) => (
                    <tr
                      class="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(AppRoutes.PollsEditId(String(item.postId)))}
                    >
                      <td class="px-4 py-3 border-r border-b border-gray-200">
                        <span class="text-sm font-medium text-gray-700">{item.question}</span>
                      </td>
                      <td class="px-4 py-3 border-r border-b border-gray-200 w-32">
                        <span
                          class={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            STATE_CLASSES[item.state]
                          }`}
                        >
                          {STATE_LABEL[item.state]}
                        </span>
                      </td>
                      <td class="px-4 py-3 border-r border-b border-gray-200 w-28">
                        <div class="flex flex-col gap-1">
                          <span class="text-sm text-gray-700">{item.totalVotes}</span>
                          <Show
                            when={item.totalVotes > 0}
                            fallback={<span class="text-xs text-gray-400">No votes yet</span>}
                          >
                            <div
                              class="flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100 cursor-pointer"
                              onMouseEnter={(e) => showPopover(item, e.currentTarget)}
                              onMouseLeave={hidePopover}
                            >
                              <For each={item.options}>
                                {(opt, i) => (
                                  <Show when={opt.voteCount > 0}>
                                    <div
                                      class={SEGMENT_BG_CLASSES[i() % SEGMENT_BG_CLASSES.length]}
                                      style={{
                                        width: `${(opt.voteCount / item.totalVotes) * 100}%`
                                      }}
                                    />
                                  </Show>
                                )}
                              </For>
                            </div>
                          </Show>
                        </div>
                      </td>
                      <td class="px-4 py-3 border-r border-b border-gray-200 w-24">
                        <span class="text-sm text-gray-500">#{item.postId}</span>
                      </td>
                      <td class="px-4 py-3 border-r border-b border-gray-200 w-64">
                        <span class="text-sm text-gray-700">{formatDate(item.closesAt)}</span>
                      </td>
                      <td class="px-4 py-3 border-r border-b border-gray-200 w-28">
                        <div class="flex justify-center items-center gap-2">
                          <Show when={item.state === 'open'}>
                            <button
                              type="button"
                              class="w-8 h-8 rounded-md flex items-center justify-center text-gray-500 border border-transparent hover:border-gray-200 hover:shadow-sm transition-all"
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteConfirmPostId(item.postId)
                              }}
                              title="Delete"
                            >
                              <Icon icon="heroicons:trash" class="text-[1rem]" />
                            </button>
                          </Show>
                        </div>
                      </td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>
        </div>
      </div>

      <Portal>
        <div
          role="tooltip"
          class="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full transition-opacity duration-300 ease-out delay-200"
          classList={{
            'opacity-100': !!hovered(),
            'opacity-0': !hovered()
          }}
          style={{
            left: `${hovered()?.x ?? -9999}px`,
            top: `${(hovered()?.y ?? 0) - 10}px`
          }}
        >
          <Show when={hovered()}>
            {(state) => (
              <>
                <div class="rounded-lg bg-gray-900 px-3 py-2.5 text-white shadow-xl ring-1 ring-black/5">
                  <div class="mb-2 max-w-[18rem] truncate text-xs font-medium text-gray-300">
                    {state().poll.question}
                  </div>
                  <ul class="flex w-64 flex-col gap-1.5">
                    <For each={state().poll.options}>
                      {(opt, i) => {
                        const pct =
                          state().poll.totalVotes > 0 ? Math.round((opt.voteCount / state().poll.totalVotes) * 100) : 0
                        return (
                          <li class="flex items-center gap-2 text-xs">
                            <span
                              class={`h-2 w-2 shrink-0 rounded-full ${
                                SEGMENT_BG_CLASSES[i() % SEGMENT_BG_CLASSES.length]
                              }`}
                            />
                            <span class="flex-1 truncate text-gray-100">{opt.text}</span>
                            <span class="shrink-0 tabular-nums text-gray-400">
                              {opt.voteCount} · {pct}%
                            </span>
                          </li>
                        )
                      }}
                    </For>
                  </ul>
                </div>
                <div class="mx-auto h-0 w-0 border-x-[6px] border-t-[6px] border-x-transparent border-t-gray-900" />
              </>
            )}
          </Show>
        </div>
      </Portal>

      <Show when={deleteConfirmPostId()}>
        <Dialog maxWidth="24rem">
          <div class="p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-2">Delete Poll</h3>
            <p class="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this poll? This action cannot be undone.
            </p>
            <div class="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmPostId(null)}
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirmPostId()!)}
                class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </Dialog>
      </Show>
    </div>
  )
}

export default PollsPage
